import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import Rating from 'modules/core/components/Rating'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import {
  AnswerQuestion,
  CustomQuestion,
} from 'modules/questionnaires/components/Answer/AnswerQuestion'
import { AnswerRatingComment } from 'modules/questionnaires/components/Answer/AnswerRating/Comment'
import {
  RatingAnswer as AnswerType,
  RatingQuestion as QuestionType,
} from 'modules/questionnaires/lib/types'

type Props = {
  question: QuestionType
  answer?: AnswerType
  position?: number
  readonly?: boolean
  onChange: (answer: AnswerType) => void
  hasAttemptedSubmit?: boolean
  renderContentSection?: CustomQuestion
}

export const AnswerRating: React.FC<Props> = ({
  question,
  answer,
  position,
  readonly,
  onChange,
  hasAttemptedSubmit = false,
  renderContentSection,
}) => {
  const [ratingAnswer, setRatingAnswer] = React.useState<AnswerType['answerRating']>({
    value: answer?.answerRating?.value ?? undefined,
    comment: answer?.answerRating?.comment ?? undefined,
  })

  const isInitialRenderRef = React.useRef(true)

  const [hoveredRating, setHoveredRating] = React.useState<number | null>(null)
  const range = question.scale.map((r) => r.value)
  const min = Math.min(...range)
  const max = Math.max(...range)

  const ratingOption = question.scale.find((option) => option.value === ratingAnswer?.value)
  const ratingLabels = question.scale.reduce<Record<string, string>>((acc, item) => {
    const label = { [item.value]: `${item.value}. ${item.text}` }
    return { ...acc, ...label }
  }, {})

  const ratingLabel = ratingOption ? ratingLabels[ratingOption.value] : ''

  useUnsafeEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      return
    }
    onChange({
      uuid: question.uuid,
      answerRating: {
        value: ratingAnswer?.value,
        comment: ratingAnswer?.comment,
      },
    })
  }, [question.uuid, ratingAnswer?.value, ratingAnswer?.comment, onChange])

  const handleRatingChange = (value: number) => {
    setRatingAnswer((prevRatingAnswer) => {
      if (prevRatingAnswer?.value === value) {
        return prevRatingAnswer
      }

      const currentValue = prevRatingAnswer?.value
      const updatedValue = value === currentValue ? null : value
      return { ...prevRatingAnswer, value: updatedValue }
    })
  }

  const handleCommentChange = useCallback((comment: string) => {
    setRatingAnswer((prevRatingAnswer) => {
      if (prevRatingAnswer?.comment === comment) {
        return prevRatingAnswer
      }

      return { ...prevRatingAnswer, comment }
    })
  }, [])

  const isMandatoryAndEmpty = question.mandatory && ratingAnswer?.value == null
  const readonlyComment = readonly || ratingAnswer?.value == null
  return (
    <AnswerQuestion
      uuid={question.uuid}
      position={position}
      title={question.title}
      description={question.description}
      renderCustomQuestion={renderContentSection}
      question={question}
    >
      <Box gap="s16">
        <Box gap="s8">
          <Rating
            aria-labelledby={`question-${question.uuid}`}
            hasTooltip
            mandatory={isMandatoryAndEmpty}
            min={min}
            max={max}
            value={ratingAnswer?.value ?? null}
            onClick={handleRatingChange}
            readonly={readonly}
            tooltipLabel={hoveredRating !== null ? ratingLabels[hoveredRating] : ''}
            onHover={setHoveredRating}
            error={
              hasAttemptedSubmit && isMandatoryAndEmpty
                ? i18n.t('performance_process.reviews.answer_evaluation.missing_mandatory_field')
                : undefined
            }
          />
          <Text.Normal size="200" weight="medium" color="grey800">
            {ratingLabel}
          </Text.Normal>
        </Box>

        {question.withComment && (
          <AnswerRatingComment
            title={question.title}
            comment={ratingAnswer?.comment}
            readonly={readonlyComment ?? false}
            onChange={handleCommentChange}
          />
        )}
      </Box>
    </AnswerQuestion>
  )
}
